import "zod/compile";
import { z } from "zod";

// Retain the existing policy ceiling. Raising it requires a code review and
// measured throughput/DB-pressure evidence; it is not a capacity guarantee.
export const WORKER_SCALING_HARD_CEILING = 20;
export const workerScalingModeSchema = z.enum(["off", "dry-run", "live"]);
export const workerScalingSettingsSchema = z
  .object({
    mode: workerScalingModeSchema,
    minWorkers: z.number().int().min(1).max(WORKER_SCALING_HARD_CEILING),
    maxWorkers: z.number().int().min(1).max(WORKER_SCALING_HARD_CEILING),
  })
  .refine((settings) => settings.maxWorkers >= settings.minWorkers, {
    path: ["maxWorkers"],
    message: "Maximum workers must be at least minimum workers",
  });
export type WorkerScalingSettings = z.infer<typeof workerScalingSettingsSchema>;

export const resolvedWorkerScalingSettingsSchema = z.object({
  settings: workerScalingSettingsSchema,
  source: z.enum(["environment", "database"]),
  revision: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
});
export type ResolvedWorkerScalingSettings = z.infer<
  typeof resolvedWorkerScalingSettingsSchema
>;

export const workerScalingStatusSchema = z.object({
  observedAt: z.iso.datetime(),
  effective: resolvedWorkerScalingSettingsSchema.nullable(),
  settingsReadHealthy: z.boolean(),
  lastEvaluationAt: z.iso.datetime().nullable(),
  currentWorkers: z.number().int().nullable(),
  readyWorkers: z.number().int().nullable(),
  recommendedWorkers: z.number().int().nullable(),
  triggerReads: z.array(z.object({ name: z.string(), healthy: z.boolean() })),
  usedFallback: z.boolean(),
  heldByCooldown: z.boolean(),
  evaluationHealthy: z.boolean(),
});
export type WorkerScalingStatus = z.infer<typeof workerScalingStatusSchema>;

export const workerScalingPageSchema = z.object({
  environment: z.string(),
  pool: z.string(),
  available: z.boolean(),
  settings: resolvedWorkerScalingSettingsSchema.nullable(),
  status: workerScalingStatusSchema.nullable(),
  statusStale: z.boolean(),
});

export type WorkerScalingPageData = z.infer<typeof workerScalingPageSchema>;
